import React, { useState, useMemo, useRef, useEffect } from 'react';
import axios from 'axios';
import { Dialog, DialogContent, DialogDescription, DialogHeader, DialogTitle } from './ui/dialog';
import { Button } from './ui/button';
import { Input } from './ui/input';
import { Label } from './ui/label';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from './ui/select';
import { toast } from 'sonner';
import { Sparkles, FileUp, RefreshCw, CheckCircle2, ScanText, Search, Check, ChevronDown } from 'lucide-react';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const CATEGORY_OPTIONS = [
  { key: 'insurance', label: 'Insurance Policy' },
  { key: 'mutual_fund', label: 'Mutual Fund' },
  { key: 'loan', label: 'Loan' },
  { key: 'recurring_deposit', label: 'Recurring Deposit' },
  { key: 'fixed_deposit', label: 'Fixed Deposit' },
  { key: 'ppf', label: 'PPF' },
  { key: 'post_office', label: 'Post Office' },
];

// Split policy fields into two groups so the review screen can lay out the
// premium triad side-by-side with its own validation. Insurance-category
// policies now store net_premium + gst_percent + total_premium separately
// (whole rupees) instead of a single premium_amount.
const BASE_FIELDS = [
  { key: 'policy_number', label: 'Policy / Product Number' },
  { key: 'insurer', label: 'Insurer / Institution' },
  { key: 'product_name', label: 'Product Name' },
  { key: 'product_type', label: 'Product Type' },
  { key: 'sum_assured', label: 'Sum Insured / Amount', type: 'number' },
  { key: 'premium_frequency', label: 'Premium Frequency' },
  { key: 'start_date', label: 'Start Date', type: 'date' },
  { key: 'end_date', label: 'End / Maturity Date', type: 'date' },
  { key: 'nominee', label: 'Nominee' },
];

const PREMIUM_FIELDS = [
  { key: 'net_premium', label: 'Net Premium (₹, whole)', type: 'number', step: 1 },
  { key: 'gst_percent', label: 'GST %', type: 'number', step: 0.5 },
  { key: 'total_premium', label: 'Total Premium (₹, whole)', type: 'number', step: 1 },
];

// All fields we round-trip through the extracted/edited payload. Used to
// seed the editable copy and to log corrections.
const ALL_FIELD_KEYS = [...BASE_FIELDS.map(f => f.key), ...PREMIUM_FIELDS.map(f => f.key)];

const confidenceTone = (score) => {
  if (typeof score !== 'number') return 'bg-slate-100 text-slate-500';
  if (score >= 0.8) return 'bg-green-100 text-green-800';
  if (score >= 0.5) return 'bg-amber-100 text-amber-800';
  return 'bg-red-100 text-red-800';
};

const toWholeRupee = (v) => {
  if (v === '' || v === null || v === undefined) return '';
  const n = Number(v);
  if (!Number.isFinite(n)) return '';
  return String(Math.round(n));
};


// Typeahead client picker — replaces the fixed-height dropdown that became
// unusable past ~30 clients. Keeps the same selected-id contract so the
// parent form submission is unchanged.
function ClientTypeahead({ clients, value, onChange, disabled }) {
  const [open, setOpen] = useState(false);
  const [query, setQuery] = useState('');
  const [highlight, setHighlight] = useState(0);
  const wrapRef = useRef(null);
  const inputRef = useRef(null);

  const selected = useMemo(
    () => clients.find((c) => c.client_id === value) || null,
    [clients, value]
  );

  // Case-insensitive filter against name, mobile and customer_code so an agent
  // can type 3 digits of a mobile or an ID suffix and still land on the right row.
  const filtered = useMemo(() => {
    const q = query.trim().toLowerCase();
    if (!q) return clients.slice(0, 50);
    return clients
      .filter((c) => {
        const haystack = `${c.name || ''} ${c.primary_mobile || c.phone || ''} ${c.customer_code || ''} ${c.email || ''}`.toLowerCase();
        return haystack.includes(q);
      })
      .slice(0, 50);
  }, [clients, query]);

  // Close the dropdown on an outside click so the layout feels native.
  useEffect(() => {
    const onDocClick = (e) => { if (wrapRef.current && !wrapRef.current.contains(e.target)) setOpen(false); };
    document.addEventListener('mousedown', onDocClick);
    return () => document.removeEventListener('mousedown', onDocClick);
  }, []);

  const pick = (c) => { onChange(c.client_id); setQuery(''); setOpen(false); };

  return (
    <div className="relative" ref={wrapRef} data-testid="doc-client-typeahead">
      <button
        type="button"
        className="w-full flex items-center justify-between gap-2 px-3 py-2 rounded-md border border-slate-200 bg-white hover:border-slate-300 text-left text-sm"
        onClick={() => { setOpen((o) => !o); setTimeout(() => inputRef.current?.focus(), 0); }}
        disabled={disabled}
        data-testid="doc-client-typeahead-button"
      >
        <span className={selected ? 'text-slate-900 truncate' : 'text-slate-400'}>
          {selected ? `${selected.name}${selected.primary_mobile ? ' · ' + selected.primary_mobile : ''}` : 'Search by name, mobile or ID…'}
        </span>
        <ChevronDown className="h-4 w-4 text-slate-500 flex-none" />
      </button>
      {open && (
        <div className="absolute z-50 mt-1 w-full bg-white border border-slate-200 rounded-md shadow-lg max-h-72 overflow-hidden flex flex-col">
          <div className="flex items-center gap-2 border-b px-3 py-2">
            <Search className="h-4 w-4 text-slate-400 flex-none" />
            <input
              ref={inputRef}
              className="flex-1 outline-none text-sm bg-transparent"
              placeholder="Type a name, mobile or ID…"
              value={query}
              onChange={(e) => { setQuery(e.target.value); setHighlight(0); }}
              onKeyDown={(e) => {
                if (e.key === 'ArrowDown') { e.preventDefault(); setHighlight((h) => Math.min(filtered.length - 1, h + 1)); }
                else if (e.key === 'ArrowUp') { e.preventDefault(); setHighlight((h) => Math.max(0, h - 1)); }
                else if (e.key === 'Enter' && filtered[highlight]) { e.preventDefault(); pick(filtered[highlight]); }
                else if (e.key === 'Escape') { setOpen(false); }
              }}
              data-testid="doc-client-typeahead-input"
            />
          </div>
          <div className="overflow-y-auto flex-1" data-testid="doc-client-typeahead-list">
            {filtered.length === 0 ? (
              <p className="text-xs text-slate-500 py-6 text-center">No matches. Try a different search.</p>
            ) : filtered.map((c, i) => (
              <button
                key={c.client_id}
                type="button"
                onClick={() => pick(c)}
                onMouseEnter={() => setHighlight(i)}
                className={`w-full flex items-center justify-between gap-2 px-3 py-2 text-left text-sm ${i === highlight ? 'bg-blue-50' : 'hover:bg-slate-50'}`}
                data-testid={`doc-client-typeahead-option-${c.client_id}`}
              >
                <div className="min-w-0 flex-1">
                  <div className="font-medium truncate">{c.name}</div>
                  <div className="text-[11px] text-slate-500 truncate">
                    {[c.customer_code, c.primary_mobile || c.phone, c.email].filter(Boolean).join(' · ') || '—'}
                  </div>
                </div>
                {c.client_id === value && <Check className="h-4 w-4 text-blue-600 flex-none" />}
              </button>
            ))}
          </div>
        </div>
      )}
    </div>
  );
}


/**
 * PDF/image → Gemini vision → editable review screen → confirm & save.
 * Insurance category persists net_premium + gst_percent + total_premium
 * separately (whole rupees) rather than a single premium_amount.
 * Always requires the agent to click Confirm — never auto-saves silently.
 */
const DocumentExtractDialog = ({ open, onClose, clients = [], onSaved }) => {
  const [step, setStep] = useState('upload'); // upload | review | saved
  const [savedRef, setSavedRef] = useState(null);     // last-saved result for the 'saved' step
  // Round 61 · Item 3 — attach the original + any additional files to the
  // saved policy via the existing /policies/{id}/documents endpoint. Checkbox
  // defaults ON. Extra files uploaded in the same post-save loop.
  const [attachOriginal, setAttachOriginal] = useState(true);
  const [extraFiles, setExtraFiles] = useState([]);
  const [category, setCategory] = useState('insurance');
  const [clientId, setClientId] = useState('');
  const [file, setFile] = useState(null);
  const [extracting, setExtracting] = useState(false);
  const [saving, setSaving] = useState(false);
  const [extracted, setExtracted] = useState(null); // { fields, confidence, notes }
  const [fields, setFields] = useState({});          // editable copy
  const [touched, setTouched] = useState(new Set()); // keys agent edited

  const reset = () => {
    setStep('upload'); setCategory('insurance'); setClientId(''); setFile(null);
    setExtracted(null); setFields({}); setTouched(new Set()); setSavedRef(null);
    setAttachOriginal(true); setExtraFiles([]);
  };

  const handleClose = () => { reset(); onClose(); };

  const runExtraction = async () => {
    if (!file) { toast.error('Please upload a PDF or image first'); return; }
    if (!clientId) { toast.error('Please select a client'); return; }
    setExtracting(true);
    try {
      const fd = new FormData();
      fd.append('file', file);
      fd.append('category', category);
      fd.append('client_id', clientId);
      const { data } = await axios.post(`${API}/documents/extract`, fd, {
        withCredentials: true, headers: { 'Content-Type': 'multipart/form-data' },
      });
      const ex = data.extracted || {};
      setExtracted(ex);
      // Seed editable copy. Premium fields are coerced to whole rupees so
      // the review screen never surfaces decimals even if the AI slipped.
      const seed = {};
      ALL_FIELD_KEYS.forEach((k) => { seed[k] = ex[k] ?? ''; });
      seed.net_premium = toWholeRupee(ex.net_premium);
      seed.total_premium = toWholeRupee(ex.total_premium);
      // gst_percent stays as a plain number string
      seed.gst_percent = ex.gst_percent === null || ex.gst_percent === undefined ? '' : String(ex.gst_percent);
      // Round 61 · Item 4 — stage left blank; the AI is NOT asked to guess it.
      seed.policy_stage = '';
      setFields(seed);
      setTouched(new Set());
      setStep('review');
      toast.success('Extracted. Please review each field before confirming.');
    } catch (e) {
      console.error('Extract failed:', e);
      toast.error(e.response?.data?.detail || 'Extraction failed. Try a clearer scan or enter manually.');
    } finally { setExtracting(false); }
  };

  const markTouched = (key) => {
    setTouched((prev) => { const next = new Set(prev); next.add(key); return next; });
  };

  const editField = (key, value) => {
    setFields((f) => ({ ...f, [key]: value }));
    if (!touched.has(key) && String(value ?? '') !== String(extracted?.[key] ?? '')) {
      markTouched(key);
    }
  };

  // Premium-specific edit — enforces whole-rupee rounding on commit (blur)
  // so an agent can still type naturally (e.g. "15" → "150") without the
  // input stealing their keystrokes mid-type.
  const commitWholeRupee = (key) => {
    const rounded = toWholeRupee(fields[key]);
    if (rounded !== fields[key]) {
      setFields((f) => ({ ...f, [key]: rounded }));
      markTouched(key);
    }
  };

  const confirm = async () => {
    // Insurance category requires a total_premium at minimum (same
    // contract as the backend confirm-save guard).
    if (category === 'insurance' && !fields.total_premium) {
      toast.error('Total Premium is required for insurance policies');
      return;
    }
    setSaving(true);
    try {
      // Normalise premiums to integers one last time before sending —
      // the backend also rounds, but a clean payload keeps the audit log honest.
      const payload = {
        client_id: clientId,
        category,
        fields: {
          ...fields,
          net_premium:   fields.net_premium   === '' ? null : Math.round(Number(fields.net_premium)),
          total_premium: fields.total_premium === '' ? null : Math.round(Number(fields.total_premium)),
          gst_percent:   fields.gst_percent   === '' ? null : Number(fields.gst_percent),
          // Round 61 · Item 4 — pass stage through; backend validates/stores.
          policy_stage:  fields.policy_stage  === '' ? null : fields.policy_stage,
        },
        corrected_fields: Array.from(touched),
        extracted_original: extracted,
      };
      const { data } = await axios.post(`${API}/documents/confirm-save`, payload, { withCredentials: true });

      // Round 61 · Item 3 — attach original + any extras to the policy via
      // the existing /policies/{id}/documents endpoint. Only runs for the
      // insurance category (financial products don't use that endpoint).
      // Each upload is wrapped so one failure doesn't block the save or the
      // remaining attachments.
      let attachedCount = 0;
      if (category === 'insurance' && data?.id) {
        const toUpload = [];
        if (attachOriginal && file) toUpload.push({ f: file, type: 'policy' });
        extraFiles.forEach((xf) => toUpload.push({ f: xf, type: 'other' }));
        for (const { f, type } of toUpload) {
          try {
            const fd = new FormData();
            fd.append('file', f);
            fd.append('doc_type', type);
            await axios.post(`${API}/policies/${data.id}/documents?doc_type=${encodeURIComponent(type)}`, fd, {
              withCredentials: true, headers: { 'Content-Type': 'multipart/form-data' },
            });
            attachedCount += 1;
          } catch (upErr) {
            console.warn('Attachment failed for', f.name, upErr?.response?.data?.detail || upErr?.message);
            toast.warning(`Couldn't attach ${f.name} (${upErr?.response?.data?.detail || 'upload error'})`);
          }
        }
      }
      toast.success(
        `Saved${data.corrections ? ` · ${data.corrections} correction${data.corrections === 1 ? '' : 's'}` : ''}` +
        (attachedCount ? ` · ${attachedCount} document${attachedCount === 1 ? '' : 's'} attached` : '')
      );
      if (onSaved) onSaved(data);
      // Round 59 · Item 3 — don't auto-close. Show the 'saved' step so the
      // agent can either file another policy for the same client or finish.
      setSavedRef(data);
      setStep('saved');
    } catch (e) {
      toast.error(e.response?.data?.detail || 'Save failed');
    } finally { setSaving(false); }
  };

  // Live computed check for the review screen — flags a mismatch between
  // net_premium × (1 + gst%) and the printed total_premium. We don't block
  // the save — Indian policy docs sometimes round differently to our math —
  // but a visible warning nudges the agent to double-check.
  const premiumWarning = useMemo(() => {
    const n = Number(fields.net_premium);
    const g = Number(fields.gst_percent);
    const t = Number(fields.total_premium);
    if (!Number.isFinite(n) || !Number.isFinite(g) || !Number.isFinite(t)) return null;
    if (n <= 0 || t <= 0) return null;
    const expected = Math.round(n * (1 + (g || 0) / 100));
    if (Math.abs(expected - t) <= 2) return null;  // ≤ ₹2 rounding slack
    return `Net ${n} + ${g}% GST = ${expected} but Total is ${t}. Trust the policy document — only override if the print total is wrong.`;
  }, [fields.net_premium, fields.gst_percent, fields.total_premium]);

  return (
    <Dialog open={open} onOpenChange={(o) => { if (!o) handleClose(); }}>
      <DialogContent className="max-w-3xl max-h-[92vh] overflow-y-auto" data-testid="doc-extract-dialog">
        <DialogHeader>
          <DialogTitle className="flex items-center gap-2">
            <ScanText className="h-5 w-5 text-purple-700" /> Extract from Document
          </DialogTitle>
          <DialogDescription>
            Upload a PDF or image of the policy/product document. Our AI will read it and pre-fill the fields —
            you just review and confirm.
          </DialogDescription>
        </DialogHeader>

        {step === 'upload' && (
          <div className="space-y-4">
            <div className="grid grid-cols-2 gap-4">
              <div className="space-y-2">
                <Label>Product Category *</Label>
                <Select value={category} onValueChange={setCategory}>
                  <SelectTrigger data-testid="doc-category-select"><SelectValue /></SelectTrigger>
                  <SelectContent>
                    {CATEGORY_OPTIONS.map(c => <SelectItem key={c.key} value={c.key}>{c.label}</SelectItem>)}
                  </SelectContent>
                </Select>
              </div>
              <div className="space-y-2">
                <Label>Client *</Label>
                <ClientTypeahead clients={clients} value={clientId} onChange={setClientId} />
              </div>
            </div>
            <div className="space-y-2">
              <Label>Document (PDF, PNG, JPEG or WebP — max 5 MB)</Label>
              <Input type="file" accept=".pdf,image/*" onChange={(e) => setFile(e.target.files?.[0] || null)} data-testid="doc-file-input" />
              {file && <p className="text-[11px] text-slate-500">{file.name} · {(file.size / 1024).toFixed(0)} KB</p>}
            </div>
            <div className="flex justify-end gap-2">
              <Button variant="outline" onClick={handleClose}>Cancel</Button>
              <Button
                onClick={runExtraction}
                disabled={extracting || !file || !clientId}
                className="bg-purple-700 hover:bg-purple-800"
                data-testid="doc-extract-button"
              >
                <Sparkles className="h-4 w-4 mr-1.5" /> {extracting ? 'Reading document…' : 'Extract with AI'}
              </Button>
            </div>
          </div>
        )}

        {step === 'review' && extracted && (
          <div className="space-y-4">
            <div className="p-2 rounded-lg border border-purple-200 bg-purple-50 text-xs text-purple-900 flex items-start gap-2" data-testid="doc-review-banner">
              <FileUp className="h-4 w-4 mt-0.5" />
              <div>
                <strong>Please review every field carefully.</strong> Corrections are logged so we can improve
                extraction accuracy over time. {extracted.notes && <span className="block mt-1 italic">Note: {extracted.notes}</span>}
              </div>
            </div>

            {/* Base fields — same two-column grid as before */}
            <div className="grid grid-cols-2 gap-3">
              {BASE_FIELDS.map(f => {
                const conf = extracted.confidence?.[f.key];
                const isTouched = touched.has(f.key);
                return (
                  <div key={f.key} className="space-y-1">
                    <div className="flex items-center justify-between">
                      <Label className="text-xs">{f.label}</Label>
                      <div className="flex items-center gap-1">
                        {isTouched && <span className="text-[9px] px-1.5 py-0.5 rounded bg-blue-100 text-blue-800">edited</span>}
                        <span className={`text-[9px] px-1.5 py-0.5 rounded ${confidenceTone(conf)}`}>
                          {typeof conf === 'number' ? `conf ${(conf * 100).toFixed(0)}%` : 'no signal'}
                        </span>
                      </div>
                    </div>
                    <Input
                      type={f.type || 'text'}
                      value={fields[f.key] ?? ''}
                      onChange={(e) => editField(f.key, e.target.value)}
                      data-testid={`doc-field-${f.key}`}
                    />
                  </div>
                );
              })}
            </div>

            {/* Premium triad — net, gst, total side-by-side with their own warning */}
            {category === 'insurance' && (
              <div className="pt-2 border-t">
                <p className="text-xs font-semibold text-slate-700 mb-2">Premium breakup (whole rupees)</p>
                <div className="grid grid-cols-3 gap-3">
                  {PREMIUM_FIELDS.map((f) => {
                    const conf = extracted.confidence?.[f.key];
                    const isTouched = touched.has(f.key);
                    return (
                      <div key={f.key} className="space-y-1">
                        <div className="flex items-center justify-between">
                          <Label className="text-xs">{f.label}</Label>
                          <div className="flex items-center gap-1">
                            {isTouched && <span className="text-[9px] px-1.5 py-0.5 rounded bg-blue-100 text-blue-800">edited</span>}
                            <span className={`text-[9px] px-1.5 py-0.5 rounded ${confidenceTone(conf)}`}>
                              {typeof conf === 'number' ? `${(conf * 100).toFixed(0)}%` : '—'}
                            </span>
                          </div>
                        </div>
                        <Input
                          type="number"
                          step={f.step || 1}
                          min={0}
                          value={fields[f.key] ?? ''}
                          onChange={(e) => editField(f.key, e.target.value)}
                          onBlur={() => f.key !== 'gst_percent' && commitWholeRupee(f.key)}
                          data-testid={`doc-field-${f.key}`}
                        />
                      </div>
                    );
                  })}
                </div>
                {premiumWarning && (
                  <p className="mt-2 text-[11px] text-amber-800 bg-amber-50 border border-amber-200 rounded px-2 py-1.5" data-testid="doc-premium-warning">
                    {premiumWarning}
                  </p>
                )}

                {/* Round 61 · Item 4 — Policy Stage picker, left blank for the agent. */}
                <div className="mt-3 space-y-1">
                  <Label className="text-xs">Policy Stage</Label>
                  <Select value={fields.policy_stage || 'not_set'} onValueChange={(v) => editField('policy_stage', v === 'not_set' ? '' : v)}>
                    <SelectTrigger data-testid="doc-field-policy_stage"><SelectValue /></SelectTrigger>
                    <SelectContent>
                      <SelectItem value="not_set">Not set (pick later)</SelectItem>
                      <SelectItem value="New Business">New</SelectItem>
                      <SelectItem value="Renewal">Renewal</SelectItem>
                      <SelectItem value="Both">Both</SelectItem>
                    </SelectContent>
                  </Select>
                  <p className="text-[10px] text-slate-500">The AI doesn't guess this — pick it so the commission tracker can match the right rate.</p>
                </div>

                {/* Round 61 · Item 3 — Attach the uploaded doc + optional extras. */}
                <div className="mt-3 p-3 rounded-md border border-blue-100 bg-blue-50/40 space-y-2" data-testid="doc-attach-panel">
                  <label className="flex items-start gap-2 cursor-pointer">
                    <input
                      type="checkbox"
                      checked={attachOriginal}
                      onChange={(e) => setAttachOriginal(e.target.checked)}
                      className="mt-0.5"
                      data-testid="doc-attach-original"
                    />
                    <span className="text-xs text-slate-700 leading-relaxed">
                      <strong>Attach this document to the policy</strong>
                      {file && <span className="block text-[10px] text-slate-500 mt-0.5">{file.name} · {(file.size / 1024).toFixed(0)} KB</span>}
                    </span>
                  </label>
                  <div className="flex items-center justify-between gap-2 pt-1 border-t border-blue-100">
                    <span className="text-xs text-slate-600">Add more files (optional)</span>
                    <input
                      type="file"
                      accept=".pdf,.jpg,.jpeg,.png,.webp"
                      multiple
                      onChange={(e) => setExtraFiles((prev) => [...prev, ...Array.from(e.target.files || [])])}
                      className="text-xs"
                      data-testid="doc-extra-files-input"
                    />
                  </div>
                  {extraFiles.length > 0 && (
                    <ul className="space-y-1 text-[11px]" data-testid="doc-extra-files-list">
                      {extraFiles.map((xf, i) => (
                        <li key={i} className="flex items-center justify-between bg-white rounded px-2 py-1 border border-blue-100">
                          <span className="truncate">{xf.name} · {(xf.size / 1024).toFixed(0)} KB</span>
                          <button
                            type="button"
                            onClick={() => setExtraFiles((prev) => prev.filter((_, idx) => idx !== i))}
                            className="text-red-600 hover:underline"
                            data-testid={`doc-extra-remove-${i}`}
                          >Remove</button>
                        </li>
                      ))}
                    </ul>
                  )}
                </div>
              </div>
            )}

            <div className="flex justify-between gap-2">
              <Button variant="outline" onClick={() => setStep('upload')} data-testid="doc-review-back">
                <RefreshCw className="h-4 w-4 mr-1.5" /> Upload a different document
              </Button>
              <Button
                onClick={confirm}
                disabled={saving}
                className="bg-green-700 hover:bg-green-800"
                data-testid="doc-confirm-save"
              >
                <CheckCircle2 className="h-4 w-4 mr-1.5" /> {saving ? 'Saving…' : `Confirm & Save${touched.size ? ` (${touched.size} corrected)` : ''}`}
              </Button>
            </div>
          </div>
        )}
        {step === 'saved' && (
          <div className="space-y-4" data-testid="doc-saved-step">
            <div className="p-4 rounded-lg border border-green-200 bg-green-50 flex items-start gap-3">
              <CheckCircle2 className="h-6 w-6 text-green-700 flex-none mt-0.5" />
              <div className="flex-1">
                <p className="text-sm font-semibold text-green-900">Policy saved for this client.</p>
                <p className="text-xs text-green-800 mt-1">
                  {savedRef?.corrections ? `${savedRef.corrections} field${savedRef.corrections === 1 ? '' : 's'} corrected during review.` : 'Saved straight from the extracted values — no edits were needed.'}
                </p>
              </div>
            </div>
            <p className="text-sm text-slate-600">
              Have another policy document for <strong>{(clients.find(c => c.client_id === clientId) || {}).name || 'this client'}</strong>?
              Jump straight to upload — the client stays selected.
            </p>
            <div className="flex justify-end gap-2">
              <Button variant="outline" onClick={handleClose} data-testid="doc-saved-done">Done</Button>
              <Button
                onClick={() => {
                  // Reset only the per-document state; keep clientId + category
                  // so the agent doesn't have to re-pick the same client.
                  setFile(null); setExtracted(null); setFields({});
                  setTouched(new Set()); setSavedRef(null); setStep('upload');
                }}
                className="bg-purple-700 hover:bg-purple-800"
                data-testid="doc-saved-add-another"
              >
                <Sparkles className="h-4 w-4 mr-1.5" /> Add another policy for this client
              </Button>
            </div>
          </div>
        )}

      </DialogContent>
    </Dialog>
  );
};

export default DocumentExtractDialog;
